import Bowser from 'bowser';
import { e2e, getQuery } from './queryUtils';

//Production is ikea.com latest and next/latest. Verification is CTE next/latest and PRs. Development is localhost. This matches the segements in DEXF Settings.
export enum Environment {
  PRODUCTION = 'production',
  PRODUCTION_NEXT_LATEST = 'production-next-latest',
  VERIFICATION = 'verification',
  VERIFICATION_PR = 'verification-pull-request',
  DEVELOPMENT = 'development',
  TEST = 'test'
}

// derived from Bowser#PLATFORMS_MAP
export type Platform = 'desktop' | 'mobile' | 'tablet' | 'tv';

export const getPlatform = () => {
  // Fake mobile for e2e tests since it's not possible to programatically set user agent in Cypress
  if (e2e() && getQuery().get('mobile') === 'true') {
    return 'mobile';
  }

  if (isIkeaApp()) {
    return 'mobile';
  }

  return Bowser.getParser(navigator.userAgent).getPlatformType(true) as Platform;
};

export const isTablet: () => boolean = () => {
  return getPlatform() === 'tablet';
};

export const isMobile: () => boolean = () => {
  return getPlatform() === 'mobile';
};

const isUiPlatform = (value: string) =>
  new URLSearchParams(window.location.search).get('uiPlatform')?.toLowerCase() ===
    value.toLowerCase() || location.pathname.includes(`/${value.toLowerCase()}/`);

export const isIkeaApp = () => isUiPlatform('app');

export const isKiosk = () => isUiPlatform('kiosk');

export const isWeb = () => !isIkeaApp() && !isKiosk();

export const getStoreId: () => string | null = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('storeId');
};

export const getKioskId: () => string | null = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('computerId');
};

export const getEnvironment = () => {
  const baseUrl: string = window.location.hostname;
  const baseHref: string = window.location.href;
  let enviroment: Environment;

  if (baseUrl.includes('cte.ikeadt') && baseHref.includes('branch')) {
    enviroment = Environment.VERIFICATION_PR;
  } else if (baseUrl.includes('cte.ikeadt') && baseHref.includes('latest')) {
    enviroment = Environment.VERIFICATION;
  } else if (import.meta.env.NODE_ENV === 'development' || baseUrl.includes('localhost')) {
    enviroment = Environment.DEVELOPMENT;
  } else if (baseUrl.includes('ikea') && baseHref.includes('next')) {
    enviroment = Environment.PRODUCTION_NEXT_LATEST;
  } else {
    enviroment = Environment.PRODUCTION;
  }
  return enviroment;
};

export const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

export const usesVirtualKeyboard = (): boolean => {
  return (
    getQuery().get('enableVirtualKeyboard') === 'true' ||
    (isTouchDevice() && isKiosk() && getQuery().get('origin') !== 'upptacka')
  );
};

export const isDevelopment = () => getEnvironment() === Environment.DEVELOPMENT;
export const isProduction = () =>
  getEnvironment() === Environment.PRODUCTION ||
  getEnvironment() === Environment.PRODUCTION_NEXT_LATEST;

export const iOS = () => {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
      navigator.platform
    ) ||
    (navigator.userAgent.includes('Mac') && isTouchDevice())
  ); // iPad on iOS 13 detection
};

export const isSafari = () => {
  const browser = Bowser.getParser(navigator.userAgent);
  return browser.satisfies({ safari: '>0' });
};
